/**
 * #979 — the card-source controller: the trader's source dictionary and each
 * card's assignment, for ONE scope at a time.
 *
 * One instance per `CardSourcesProvider`, mirroring #978's notes controller:
 * deliberately not a module-level store, so the normal route (which mounts no
 * provider) holds no source state and makes no request.
 *
 * ─── Invariants ───────────────────────────────────────────────────────────────
 *
 * - SCOPE ISOLATION. `setScope` bumps `generation` and drops the dictionary,
 *   every assignment, every in-flight write marker and every issue. Every async
 *   result compares the generation it started under, so a late answer from the
 *   old account/org can never populate the new one.
 * - NO TICK TRAFFIC. Assignments are read only when a control for a never-seen
 *   target registers, batched per microtask in bounded chunks. The dictionary
 *   is read once per scope, then again only on an explicit event: a menu
 *   opening (fresh on look, not on tick), or a write answer naming a source
 *   this list lacks.
 * - THE NEWEST LIST WINS, AND KEEPS WHAT WAS ADDED MEANWHILE. Every dictionary
 *   read is numbered; only the latest request's answer applies, and names this
 *   client created after that request started are kept even if the answer
 *   predates them.
 * - A READ NEVER OVERWRITES A WRITE. Read results only fill entries still
 *   `loading`; a write's canonical assignment is authoritative once it lands.
 * - NO PREMATURE LABEL. A card shows a source only from a canonical
 *   assignment the adapter returned, never from the choice still in flight.
 * - ONE WRITE PER TARGET. While a target's write is in flight, further
 *   assign/clear calls for it are refused, so repeated clicks cannot send
 *   contradictory writes.
 * - NO SILENT REMAP. An assignment whose source id is absent from the
 *   dictionary is reported as unknown; it is never matched to another entry.
 * - FOREIGN SCOPE IS INERT. A target from another scope (a control that
 *   rendered in the same commit as a switch) is ignored: no read, no write.
 * - CREATE NEVER SELECTS, AND NEVER TWICE. A create only adds the returned
 *   source to the dictionary; no assignment changes. While one is in flight,
 *   further creates are refused, so Enter-then-click cannot add two.
 */
import { createStore, type StoreApi } from 'zustand/vanilla';
import { checkSourceName } from './sourceName';
import {
  CardSourceWriteError,
  targetKey,
  type CardSource,
  type CardSourceAdapter,
  type CardSourceAssignment,
  type CardSourceTarget,
} from './types';

/** Upper bound on one `adapter.readAssignments` call. */
export const READ_BATCH_SIZE = 50;

export type DictionaryState =
  | { status: 'idle' }
  | { status: 'loading'; sources: readonly CardSource[] | null }
  | { status: 'ready'; sources: readonly CardSource[] }
  | { status: 'error'; sources: readonly CardSource[] | null };

export type AssignmentEntry =
  | { status: 'loading' }
  | { status: 'ready'; assignment: CardSourceAssignment }
  | { status: 'error' };

/** Why the last write for a target did not take, until the next attempt. */
export type WriteIssue = 'failed' | 'forbidden' | 'unknown_source' | 'conflict';

export interface CardSourcesState {
  generation: number;
  /** null = no resolved identity; every control renders disabled. */
  scope: string | null;
  dictionary: DictionaryState;
  entries: Readonly<Record<string, AssignmentEntry>>;
  /** Target keys with a write in flight. */
  writing: Readonly<Record<string, true>>;
  issues: Readonly<Record<string, WriteIssue>>;
  /** A dictionary create is in flight. */
  creating: boolean;
}

/** What a create resolved to, for the add dialog to show. */
export type CreateOutcome =
  | { ok: true; source: CardSource; reused: boolean }
  | { ok: false; reason: 'empty' | 'too_long' | 'invalid_name' | 'failed' | 'busy' | 'stale' };

export interface CardSourcesController {
  store: StoreApi<CardSourcesState>;
  setScope(scope: string | null): void;
  /** A control mounted for `target`: reads it (once) and the dictionary. */
  register(target: CardSourceTarget): void;
  /** Re-read the dictionary (menu open, or Retry after a failed read). */
  refreshDictionary(): void;
  retryRead(target: CardSourceTarget): void;
  /** Set (`sourceId`) or clear (`null`) the target's source. */
  assign(target: CardSourceTarget, sourceId: string | null): Promise<void>;
  /** Add a name to the dictionary (never assigns it). */
  createSource(name: string): Promise<CreateOutcome>;
}

function unassigned(target: CardSourceTarget): CardSourceAssignment {
  return { target, sourceId: null, revision: 0, updatedAt: null };
}

/** The last good list, carried through loading/error so labels don't flicker. */
export function knownSources(d: DictionaryState): readonly CardSource[] | null {
  return d.status === 'idle' ? null : d.sources;
}

export function createCardSourcesController(
  adapter: CardSourceAdapter,
  initialScope: string | null,
): CardSourcesController {
  const store = createStore<CardSourcesState>(() => ({
    generation: 0,
    scope: initialScope,
    dictionary: { status: 'idle' },
    entries: {},
    writing: {},
    issues: {},
    creating: false,
  }));
  let pending = new Map<string, CardSourceTarget>();
  let flushScheduled = false;
  /** Numbers dictionary reads, so only the latest request's answer applies. */
  let listSeq = 0;
  /** Sources this client created in the current scope, in creation order. */
  let created: CardSource[] = [];

  const setEntries = (updates: Record<string, AssignmentEntry>) =>
    store.setState((s) => ({ entries: { ...s.entries, ...updates } }));

  const flush = () => {
    flushScheduled = false;
    const batch = [...pending.values()];
    pending = new Map();
    const generation = store.getState().generation;
    for (let i = 0; i < batch.length; i += READ_BATCH_SIZE) {
      const chunk = batch.slice(i, i + READ_BATCH_SIZE);
      const settleChunk = (status: (t: CardSourceTarget, found?: CardSourceAssignment) => AssignmentEntry, found?: Map<string, CardSourceAssignment>) => {
        if (store.getState().generation !== generation) return;
        const { entries } = store.getState();
        const updates: Record<string, AssignmentEntry> = {};
        for (const t of chunk) {
          const key = targetKey(t);
          if (entries[key]?.status === 'loading') updates[key] = status(t, found?.get(key));
        }
        setEntries(updates);
      };
      adapter.readAssignments(chunk).then(
        (rows) =>
          settleChunk(
            (t, row) => ({ status: 'ready', assignment: row ?? unassigned(t) }),
            new Map(rows.map((r) => [targetKey(r.target), r])),
          ),
        () => settleChunk(() => ({ status: 'error' })),
      );
    }
  };

  const requestRead = (target: CardSourceTarget) => {
    const key = targetKey(target);
    setEntries({ [key]: { status: 'loading' } });
    pending.set(key, target);
    if (!flushScheduled) {
      flushScheduled = true;
      queueMicrotask(flush);
    }
  };

  const loadDictionary = () => {
    const { generation, dictionary } = store.getState();
    const seq = ++listSeq;
    const createdBefore = created.length;
    const current = () => store.getState().generation === generation && seq === listSeq;
    store.setState({ dictionary: { status: 'loading', sources: knownSources(dictionary) } });
    adapter.listSources().then(
      (sources) => {
        if (!current()) return;
        // A create that landed after this request left is not in its answer.
        const later = created.slice(createdBefore).filter((c) => !sources.some((x) => x.id === c.id));
        store.setState({ dictionary: { status: 'ready', sources: [...sources, ...later] } });
      },
      () => {
        if (!current()) return;
        store.setState((s) => ({ dictionary: { status: 'error', sources: knownSources(s.dictionary) } }));
      },
    );
  };

  const inScope = (target: CardSourceTarget) => target.scope === store.getState().scope;

  const setIssue = (key: string, issue: WriteIssue | null) =>
    store.setState((s) => {
      const issues = { ...s.issues };
      if (issue) issues[key] = issue;
      else delete issues[key];
      return { issues };
    });

  const setWriting = (key: string, on: boolean) =>
    store.setState((s) => {
      const writing = { ...s.writing };
      if (on) writing[key] = true;
      else delete writing[key];
      return { writing };
    });

  return {
    store,
    setScope(scope) {
      if (store.getState().scope === scope) return;
      pending = new Map();
      created = [];
      store.setState((s) => ({
        generation: s.generation + 1,
        scope,
        dictionary: { status: 'idle' },
        entries: {},
        writing: {},
        issues: {},
        creating: false,
      }));
    },
    register(target) {
      if (!inScope(target)) return;
      if (store.getState().dictionary.status === 'idle') loadDictionary();
      if (!store.getState().entries[targetKey(target)]) requestRead(target);
    },
    refreshDictionary: loadDictionary,
    retryRead(target) {
      if (!inScope(target)) return;
      if (store.getState().entries[targetKey(target)]?.status === 'error') requestRead(target);
    },
    async assign(target, sourceId) {
      if (!inScope(target)) return;
      const key = targetKey(target);
      const { entries, writing, generation } = store.getState();
      const entry = entries[key];
      if (entry?.status !== 'ready' || writing[key]) return;
      if (entry.assignment.sourceId === sourceId) return;
      setWriting(key, true);
      setIssue(key, null);
      try {
        const saved = await adapter.assign(target, sourceId, entry.assignment.revision);
        if (store.getState().generation !== generation) return;
        // An answer for a different target must never be painted on this one.
        if (targetKey(saved.target) !== key) return setIssue(key, 'failed');
        setEntries({ [key]: { status: 'ready', assignment: saved } });
      } catch (err) {
        if (store.getState().generation !== generation) return;
        if (
          err instanceof CardSourceWriteError &&
          err.reason === 'conflict' &&
          err.latest &&
          targetKey(err.latest.target) === key
        ) {
          // The winner is real state: show it, and say this card changed elsewhere.
          setEntries({ [key]: { status: 'ready', assignment: err.latest } });
          setIssue(key, 'conflict');
          // A winner naming a source we have never listed needs a fresh list.
          const known = knownSources(store.getState().dictionary);
          if (err.latest.sourceId && !known?.some((s) => s.id === err.latest!.sourceId)) loadDictionary();
        } else if (err instanceof CardSourceWriteError && err.reason === 'unknown_source') {
          setIssue(key, 'unknown_source');
          loadDictionary();
        } else if (err instanceof CardSourceWriteError && err.reason === 'forbidden') {
          setIssue(key, 'forbidden');
        } else {
          setIssue(key, 'failed');
        }
      } finally {
        if (store.getState().generation === generation) setWriting(key, false);
      }
    },
    async createSource(name) {
      const check = checkSourceName(name);
      if (!check.ok) return { ok: false, reason: check.reason };
      const { generation, creating } = store.getState();
      if (creating) return { ok: false, reason: 'busy' };
      store.setState({ creating: true });
      try {
        const { source, reused } = await adapter.createSource(check.name);
        if (store.getState().generation !== generation) return { ok: false, reason: 'stale' };
        if (!created.some((c) => c.id === source.id)) created.push(source);
        // Never read yet: a list of just this name would make every other
        // assigned card look "unknown" — read the real list instead.
        if (store.getState().dictionary.status === 'idle') loadDictionary();
        store.setState((s) => {
          const known = knownSources(s.dictionary) ?? [];
          const sources = known.some((k) => k.id === source.id) ? known : [...known, source];
          return { dictionary: { ...s.dictionary, status: s.dictionary.status === 'idle' ? 'ready' : s.dictionary.status, sources } as DictionaryState };
        });
        return { ok: true, source, reused };
      } catch (err) {
        if (store.getState().generation !== generation) return { ok: false, reason: 'stale' };
        const invalid = err instanceof CardSourceWriteError && err.reason === 'invalid_name';
        return { ok: false, reason: invalid ? 'invalid_name' : 'failed' };
      } finally {
        if (store.getState().generation === generation) store.setState({ creating: false });
      }
    },
  };
}
